import { useThemeStore, type ThemeMode } from '@core/state/theme.store';

import { Icon } from './Icon';
import { SegmentedControl } from './SegmentedControl';

/**
 * Light / Dark / System, wherever a person might look for it.
 *
 * The profile menu hosts it today. It is a component rather than three menu
 * items so a settings page, or a command palette, can offer the same control
 * without re-deciding its labels or its order.
 */

const OPTIONS: Array<{ value: ThemeMode; label: string; icon: string }> = [
  { value: 'light', label: 'Light', icon: 'light_mode' },
  { value: 'dark', label: 'Dark', icon: 'dark_mode' },
  { value: 'system', label: 'System', icon: 'contrast' }
];

export interface ThemeSwitchProps {
  /** Icons only, for tight spots; the names stay as accessible labels. */
  compact?: boolean;
  className?: string;
}

export function ThemeSwitch({ compact = false, className }: ThemeSwitchProps) {
  const mode = useThemeStore((state) => state.mode);
  const setMode = useThemeStore((state) => state.setMode);

  return (
    <SegmentedControl<ThemeMode>
      aria-label="Theme"
      size="sm"
      block={!compact}
      className={className}
      value={mode}
      onChange={setMode}
      options={OPTIONS.map((option) => ({
        value: option.value,
        icon: <Icon>{option.icon}</Icon>,
        label: compact ? null : option.label,
        ariaLabel: compact ? option.label : undefined
      }))}
    />
  );
}

export default ThemeSwitch;
